import * as lodash from 'lodash'
import * as moment from 'moment'

export const AirbnbPromotionType = 'NEW_LISTING_PROMOTION' as const
export const AirbnbTopRatedGuestDiscountPromotionType = 'HIGH_RATED_GUEST_DISCOUNT' as const
export const AirbnbMobileOnlyPromotionType = 'MOBILE_ONLY_DISCOUNT' as const

export type AirbnbPromotionTypeValue =
  | typeof AirbnbPromotionType
  | typeof AirbnbTopRatedGuestDiscountPromotionType
  | typeof AirbnbMobileOnlyPromotionType

export enum AirbnbPromotionStatus {
  AVAILABLE = 'AVAILABLE',
  ONGOING = 'ONGOING',
  EXPIRED = 'EXPIRED',
}

export interface AirbnbListingPromotionItem {
  type: AirbnbPromotionTypeValue
  listing_id: string
  status: AirbnbPromotionStatus
}

export interface AirbnbListingScopedPromotionItem {
  promotion_id: string
  listing_id: string
  type: AirbnbPromotionTypeValue
  promotion_settings?: Record<string, any>
}

// Airbnb blackout-date quota of one discount for the current calendar year.
export interface AirbnbBlackoutDatesQuota {
  dates_used_count: number
  dates_available_count: number
}

// Airbnb's launch quota, per listing, per discount, per calendar year.
export const AirbnbBlackoutDatesYearlyQuota = 30

export interface AirbnbListingPromotionProgramStatus {
  type: AirbnbPromotionTypeValue
  listing_id: string
  enabled: boolean
  discount_percent: 10 | 15
  promotion_id?: string
  blackout_dates?: string[] // YYYY-MM-DD
  blackout_dates_quota?: AirbnbBlackoutDatesQuota
  promotion?: AirbnbListingScopedPromotionItem
  promotions?: AirbnbListingScopedPromotionItem[]
  synced_at?: number
}

export interface AirbnbTopRatedGuestDiscountStatus extends AirbnbListingPromotionProgramStatus {
  type: typeof AirbnbTopRatedGuestDiscountPromotionType
  discount_percent: 15
}

export interface AirbnbMobileOnlyDiscountStatus extends AirbnbListingPromotionProgramStatus {
  type: typeof AirbnbMobileOnlyPromotionType
  discount_percent: 10
}

export interface AirbnbBlackoutDatesYearQuota {
  year: string
  total: number
  left: number
}

// Quota of each year that can have blackout dates: Airbnb accepts dates up to 2 years ahead.
// Airbnb reports the quota of the year when the status was read (synced_at). Other years count
// the listed dates. `dates` is the selection to check; by default, the dates saved on Airbnb.
export function getAirbnbBlackoutDatesYearQuotas(
  discount: AirbnbListingPromotionProgramStatus,
  dates = discount.blackout_dates || []
): AirbnbBlackoutDatesYearQuota[] {
  const quota = discount.blackout_dates_quota
  const total = quota ? quota.dates_used_count + quota.dates_available_count : AirbnbBlackoutDatesYearlyQuota
  const quotaYear = (discount.synced_at ? moment.unix(discount.synced_at) : moment()).format('YYYY')
  const savedByYear = lodash.countBy(discount.blackout_dates || [], (date) => date.slice(0, 4))
  const selectedByYear = lodash.countBy(dates, (date) => date.slice(0, 4))

  return lodash.range(moment().year(), moment().add(2, 'years').year() + 1).map((y) => {
    const year = `${y}`
    const saved = savedByYear[year] || 0
    const used = (year === quotaYear && quota ? quota.dates_used_count : saved) - saved + (selectedByYear[year] || 0)
    return { year, total, left: total - used }
  })
}

export interface AirbnbBlackoutDateRange {
  from: string
  to: string
  dates: string[]
  label: string
}

// Groups blackout dates (YYYY-MM-DD) into ranges of consecutive days.
export function toAirbnbBlackoutDateRanges(dates: string[]): AirbnbBlackoutDateRange[] {
  const ranges: AirbnbBlackoutDateRange[] = []
  for (const date of lodash.uniq(dates || []).sort()) {
    const last = lodash.last(ranges)
    if (last && moment(last.to).add(1, 'day').format('YYYY-MM-DD') === date) {
      last.to = date
      last.dates.push(date)
    } else {
      ranges.push({ from: date, to: date, dates: [date], label: '' })
    }
  }

  ranges.forEach((range) => {
    const from = moment(range.from)
    const to = moment(range.to)
    range.label =
      range.from === range.to
        ? to.format('MMM D, YYYY')
        : `${from.format(from.isSame(to, 'year') ? 'MMM D' : 'MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`
  })
  return ranges
}

export interface AirbnbListingDiscountsResponse {
  top_rated_guest_discount: AirbnbTopRatedGuestDiscountStatus
  mobile_only_discount: AirbnbMobileOnlyDiscountStatus
}
